import { useState, useEffect, useCallback } from "react";
import type { AssetGroup, AssetsLayout } from "@/types/types";
import { DropResult } from "react-beautiful-dnd";
import { balanceSheetActions } from "@/redux/balance-sheet/balance-sheet-slice";
import { HouseholdDataType } from "@/types/enums";
import { toast } from "react-toastify";
import { useHouseholdDataUpdate } from "@/hooks/useHouseholdDataUpdate";
import { useAppDispatch } from "@/hooks/useAppDispatch";

type Props = {
  assetGroups: AssetGroup[] | undefined;
  householdExternalCode: string;
  layout: AssetsLayout;
};

export const useAssetDragAndDrop = ({ assetGroups, householdExternalCode, layout }: Props) => {
  const leftItems = layout?.leftColumn.map((externalCode) =>
    assetGroups?.find((group) => group.externalCode === externalCode)
  );
  const rightItems = layout?.rightColumn.map((externalCode) =>
    assetGroups?.find((group) => group.externalCode === externalCode)
  );

  const [leftColumnItems, setLeftColumnItems] = useState<AssetGroup[]>(leftItems ?? []);
  const [rightColumnItems, setRightColumnItems] = useState<AssetGroup[]>(rightItems ?? []);
  const dispatch = useAppDispatch();

  const { mutateAsync, isPending, isSuccess } = useHouseholdDataUpdate(
    householdExternalCode as string,
    HouseholdDataType.AssetsLayout
  );

  const handleBeforeUnload = useCallback((e: BeforeUnloadEvent) => {
    e.preventDefault();
  }, []);

  useEffect(() => {
    if (isPending) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    if (isSuccess) {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isPending, isSuccess, handleBeforeUnload]);

  const saveAssetsLayoutHandler = async (previousState: {
    previousLeftColumnItems: AssetGroup[];
    previousRightColumnItems: AssetGroup[];
  }) => {
    try {
      await mutateAsync({
        data: JSON.stringify({ leftColumnItems, rightColumnItems }),
      });

      dispatch(balanceSheetActions.setIsAssetsLayoutChanged(false));
    } catch (error) {
      toast.error("Failed to save assets layout");

      setLeftColumnItems(previousState.previousLeftColumnItems);
      setRightColumnItems(previousState.previousRightColumnItems);
    }
  };

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    if (!destination) return;

    const isValidMove = () => {
      const destinationItem =
        destination.droppableId === "left"
          ? leftColumnItems[destination.index]
          : rightColumnItems[destination.index];

      if (destinationItem?.type === "Individual" || destinationItem?.type === "Joint") return false;

      return true;
    };

    if (!isValidMove()) return;

    const previousLeftColumnItems = [...leftColumnItems];
    const previousRightColumnItems = [...rightColumnItems];

    dispatch(balanceSheetActions.setIsAssetsLayoutChanged(true));

    const sourceItems = source.droppableId === "left" ? leftColumnItems : rightColumnItems;
    const destItems = destination.droppableId === "left" ? leftColumnItems : rightColumnItems;
    const [removed] = sourceItems.splice(source.index, 1);

    if (!removed) return;
    destItems.splice(destination.index, 0, removed);

    if (source.droppableId === "left") {
      setLeftColumnItems([...sourceItems]);
    } else {
      setRightColumnItems([...sourceItems]);
    }

    saveAssetsLayoutHandler({ previousLeftColumnItems, previousRightColumnItems });
  };

  return { leftColumnItems, rightColumnItems, onDragEnd };
};
